/**
 * usePlaybookExtractionPubSub
 *
 * Hook that connects to the `ai_processing` PubSub hub, joins the
 * `playbook_{playbookUuid}` group, and routes incoming extraction progress
 * events into Redux (REVIEW_EXTRACT_*) actions. On completion or failure
 * the hook auto-refreshes the playbook detail.
 *
 * Usage in ReviewDetail:
 *   usePlaybookExtractionPubSub(playbookUuid, isExtractingPlaybook);
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch } from '../../store';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import {
  fetchReviewDetail,
  REVIEW_EXTRACT_CONNECTING,
  REVIEW_EXTRACT_CONNECTED,
  REVIEW_EXTRACT_STEP,
  REVIEW_EXTRACT_COMPLETED,
  REVIEW_EXTRACT_FAILED,
  REVIEW_EXTRACT_RESET,
  REVIEW_EXTRACTING_PLAYBOOK,
} from '../../store/actions/Review/ReviewActions';

const HUB = 'ai_processing';

export function usePlaybookExtractionPubSub(
  playbookUuid: string | undefined,
  enabled: boolean,
) {
  const dispatch = useAppDispatch();
  const groupRef = useRef<string | null>(null);
  const connectedRef = useRef(false);

  // ── Handle incoming PubSub messages ──
  const handleMessage = useCallback(
    (raw: any, _eventName?: string) => {
      const event = Array.isArray(raw) ? raw[0] : raw || {};
      const innerData = event.data || {};
      const data = { ...event, ...innerData };

      const rawStatus = data.status ?? '';
      const status = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : String(rawStatus).toLowerCase();
      const subType = data.sub_type || data.event_name || data.eventType || data.event_type || '';
      const stepName = data.step_name || data.stepName || data.step || '';
      const message = data.message || '';
      const progressPercent = data.progress_percent;
      const eventPlaybookUuid = data.playbook_uuid || data.playbookUuid || null;

      // Only process events for our playbook
      if (eventPlaybookUuid && playbookUuid && eventPlaybookUuid !== playbookUuid) return;

      // Skip technical/system messages
      const isTechnical =
        (stepName === 'system' || subType === 'system' || subType === 'connected' || subType === 'disconnected') &&
        !message;
      if (isTechnical) return;

      const isCompletion =
        status === 'completed' || subType === 'completed' || subType === 'complete' || data.event_name === 'ai_processing_complete';
      const isFailure = status === 'failed' || subType === 'failed' || subType === 'step_failed' || !!data.error;

      console.log('[PlaybookExtraction PubSub]', { subType, stepName, message, progressPercent, status });

      if (isCompletion) {
        dispatch({ type: REVIEW_EXTRACT_COMPLETED, payload: { ok: true, summary: message || 'Extraction complete' } });
        dispatch({ type: REVIEW_EXTRACTING_PLAYBOOK, payload: false });

        // Leave group
        if (groupRef.current) {
          const svc = getMultiHubSocketService();
          svc.emit(HUB, 'leave', { group: groupRef.current });
          groupRef.current = null;
        }

        // Refetch detail to pick up the new rules
        if (playbookUuid) {
          setTimeout(() => {
            dispatch(fetchReviewDetail(playbookUuid) as any);
          }, 1500);
        }
        return;
      }

      if (isFailure) {
        dispatch({ type: REVIEW_EXTRACT_FAILED, payload: { error: message || data.error || 'Extraction failed' } });
        dispatch({ type: REVIEW_EXTRACTING_PLAYBOOK, payload: false });

        // Leave group
        if (groupRef.current) {
          const svc = getMultiHubSocketService();
          svc.emit(HUB, 'leave', { group: groupRef.current });
          groupRef.current = null;
        }
        return;
      }

      // Progress update — dispatch step
      dispatch({
        type: REVIEW_EXTRACT_STEP,
        payload: {
          id: stepName || `step_${data.step_index || 0}`,
          name: stepName,
          status: subType === 'step_started' ? 'in_progress' : subType === 'step_completed' ? 'completed' : 'in_progress',
          message,
        },
      });
    },
    [dispatch, playbookUuid],
  );

  // ── Connect and join group when extraction starts ──
  useEffect(() => {
    if (!enabled || !playbookUuid) return;

    let mounted = true;
    const groupName = `playbook_${playbookUuid}`;

    dispatch({ type: REVIEW_EXTRACT_CONNECTING });

    const connect = async () => {
      const svc = getMultiHubSocketService();
      const connected = await svc.connectToHub(HUB);
      if (!connected || !mounted) return;

      connectedRef.current = true;
      svc.on(HUB, '*', handleMessage);

      // Join the extraction group
      svc.emit(HUB, 'join', { group: groupName });
      groupRef.current = groupName;

      dispatch({ type: REVIEW_EXTRACT_CONNECTED });
      console.log(`[PlaybookExtraction PubSub] Joined group ${groupName}`);
    };

    connect();

    return () => {
      mounted = false;
      const svc = getMultiHubSocketService();

      if (groupRef.current) {
        svc.emit(HUB, 'leave', { group: groupRef.current });
        groupRef.current = null;
      }

      svc.off(HUB, '*', handleMessage);
      connectedRef.current = false;
    };
  }, [enabled, playbookUuid, handleMessage, dispatch]);

  // ── Reset extraction progress when component unmounts ──
  useEffect(() => {
    return () => {
      dispatch({ type: REVIEW_EXTRACT_RESET });
    };
  }, [dispatch]);
}
